import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { money, shortDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, Badge } from "@/components/kit";
import { CaretLeft, HandCoins } from "@phosphor-icons/react";

export default function LoanDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [loan, setLoan] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [repayments, setRepayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [collectOpen, setCollectOpen] = useState(false);
  const [colForm, setColForm] = useState({
    amount: "", payment_method: "cash", reference_no: "", note: "",
    denom_500: 0, denom_200: 0, denom_100: 0, denom_50: 0, denom_20: 0, denom_10: 0, denom_5: 0,
  });
  const [colBusy, setColBusy] = useState(false);
  const [colErr, setColErr] = useState("");
  const [colDone, setColDone] = useState(null);

  const openCollectToday = () => {
    const suggested = Math.min(Number(loan?.emi) || 0, Number(loan?.outstanding) || 0);
    setColForm((f) => ({
      ...f,
      amount: suggested > 0 ? String(suggested) : "",
      payment_method: "cash",
      reference_no: "",
      note: "",
      denom_500: 0, denom_200: 0, denom_100: 0, denom_50: 0, denom_20: 0, denom_10: 0, denom_5: 0,
    }));
    setColErr(""); setColDone(null); setCollectOpen(true);
  };

  const submitCollectToday = async (e) => {
    e.preventDefault();
    setColErr(""); setColBusy(true);
    try {
      const body = {
        customer_id: loan.customer_id,
        kind: "loan_repayment",
        account_id: loan.id,
        amount: Number(colForm.amount),
        note: colForm.note || null,
        payment_method: colForm.payment_method,
      };
      if (colForm.payment_method === "cash") {
        ["500","200","100","50","20","10","5"].forEach((d) => { body[`denom_${d}`] = Number(colForm[`denom_${d}`]) || 0; });
      } else if (colForm.reference_no) {
        body.reference_no = colForm.reference_no;
      }
      const { data } = await api.post("/collections", body);
      // reload the loan FIRST so the success screen shows the fresh outstanding
      await load();
      setColDone(data);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setColErr(Array.isArray(d) ? d.map((x) => x.msg || x).join(", ") : (d || "Failed to record"));
    } finally { setColBusy(false); }
  };

  const load = async () => {
    const l = await api.get(`/loans/${id}`);
    setLoan(l.data);
    const [c, r] = await Promise.all([
      api.get(`/customers/${l.data.customer_id}`).catch(() => ({ data: null })),
      api.get(`/loans/${id}/repayments`),
    ]);
    setCustomer(c.data);
    setRepayments(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post(`/loans/${id}/repay`, { amount: Number(amount), note });
      setOpen(false); setAmount(""); setNote("");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  if (!loan) return <div className="p-8 text-slate-500">Loading…</div>;

  const canPay = ["super_admin", "branch_admin", "staff"].includes(user.role);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <Link to="/loans" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 hover:text-slate-800 print:hidden">
        <CaretLeft size={14} /> Back to loans
      </Link>
      <div className="print-header">
        <div className="text-lg font-bold text-emerald-800">Ujjwal Multipurpose Cooperative Society Ltd</div>
        <div className="text-sm text-slate-600">Loan Ledger / Repayment Schedule</div>
      </div>
      <PageHeader
        title={<>{typeLabel(loan.loan_type)} <span className="text-slate-400 text-lg mono ml-2">{loan.loan_no || `#${loan.id.slice(0, 8)}`}</span></>}
        subtitle={customer && <Link to={`/customers/${customer.id}`} className="text-emerald-700 hover:underline">{customer.name} · <span className="mono">{customer.phone}</span></Link>}
        action={canPay && loan.status !== "closed" && (
          <div className="flex gap-2 flex-wrap">
            <Button onClick={openCollectToday} data-testid="collect-today-btn">
              <HandCoins size={16} weight="fill" /> Collect today
            </Button>
            <Button variant="secondary" onClick={() => setOpen(true)} data-testid="record-repayment-btn">Record repayment</Button>
            <Link to="/loans/closure" className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="go-loan-closure">
              Close / Pre-close
            </Link>
            <button type="button" onClick={() => window.print()} className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 print:hidden" data-testid="print-loan">🖨 Print</button>
          </div>
        )}
      />

      {loan.file_charge_amount ? (
        <Card className="p-4 mb-4 border-sky-200 bg-sky-50/50" data-testid="file-charge-card">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Society file charge</div>
          <div className="text-sm">
            <span className="mono font-semibold text-sky-800">{money(loan.file_charge_amount)}</span>
            <span className="text-slate-500 ml-2">
              ({loan.file_charge_type === "percent"
                ? `${loan.file_charge_value}% of principal`
                : `flat ₹${loan.file_charge_value}`})
            </span>
          </div>
        </Card>
      ) : null}

      {loan.status === "closed" && loan.closure_type && (
        <Card className={`p-4 mb-4 ${loan.closure_type === "preclosed" ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="loan-closed-banner">
          <div className="text-sm">
            {loan.closure_type === "preclosed" ? "Pre-closed" : "Closed"} on {shortDate(loan.closed_at)}
            {loan.preclosure_charge ? <> · pre-closure charge <span className="mono font-semibold">{money(loan.preclosure_charge)}</span></> : null}
            {loan.settlement_amount ? <> · settlement <span className="mono font-semibold">{money(loan.settlement_amount)}</span></> : null}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatSm label="Principal" value={money(loan.principal)} />
        <StatSm label="EMI (daily)" value={money(loan.emi)} accent />
        <StatSm label="Outstanding" value={money(loan.outstanding)} testId="loan-outstanding" />
        <StatSm label="Total interest" value={money(loan.total_interest)} />
        <StatSm label="Status" node={loan.status === "closed" ? <Badge tone="success">Closed</Badge> : (loan.overdue_count > 0 ? <Badge tone="danger">{loan.overdue_count} overdue</Badge> : <Badge tone="neutral">Active</Badge>)} />
      </div>

      {loan.loan_type === "commodity" && loan.commodity_name && (
        <Card className="p-4 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Commodity</div>
          <div className="text-sm"><span className="font-medium">{loan.commodity_name}</span>{loan.commodity_value ? <span className="text-slate-500 ml-2 mono">· value {money(loan.commodity_value)}</span> : null}</div>
        </Card>
      )}
      {loan.loan_type === "deposit" && loan.linked_savings_id && (
        <Card className="p-4 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Linked deposit</div>
          <Link to={`/savings/${loan.linked_savings_id}`} className="text-sm text-emerald-700 hover:underline mono">
            FD #{loan.linked_savings_id.slice(0, 8)} →
          </Link>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="heading font-semibold">EMI Schedule</h3>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">Due</th>
                  <th className="text-right px-4 py-2">EMI</th>
                  <th className="text-right px-4 py-2">Principal</th>
                  <th className="text-right px-4 py-2">Interest</th>
                  <th className="text-right px-4 py-2">Balance</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loan.schedule.map((s) => {
                  const isOverdue = s.status !== "paid" && s.due_date < today;
                  return (
                    <tr key={s.installment_no} className="border-t border-slate-100">
                      <td className="px-4 py-2 mono text-slate-400">{s.installment_no}</td>
                      <td className="px-4 py-2 mono">{s.due_date}</td>
                      <td className="px-4 py-2 mono text-right">{money(s.emi)}</td>
                      <td className="px-4 py-2 mono text-right text-slate-600">{money(s.principal)}</td>
                      <td className="px-4 py-2 mono text-right text-slate-600">{money(s.interest)}</td>
                      <td className="px-4 py-2 mono text-right">{money(s.balance)}</td>
                      <td className="px-4 py-2">
                        {s.status === "paid" ? <Badge tone="success">Paid</Badge> : isOverdue ? <Badge tone="danger">Overdue</Badge> : <Badge tone="neutral">Pending</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="heading font-semibold">Repayments</h3>
          </div>
          {repayments.length === 0 ? (
            <div className="text-sm text-slate-500 p-6 text-center">No repayments yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {repayments.map((r) => {
                const label = r.type === "file_charge" ? "File charge"
                  : r.type === "preclosure_charge" ? "Pre-closure charge"
                  : r.type === "closure_settlement" ? "Closure settlement"
                  : "Repayment";
                const isCharge = r.type === "file_charge" || r.type === "preclosure_charge";
                return (
                  <div key={r.id} className="px-5 py-3 flex justify-between items-center">
                    <div>
                      <div className={`mono font-medium ${isCharge ? "text-sky-800" : "text-emerald-700"}`}>
                        {isCharge ? "+ " : ""}{money(r.amount)}
                      </div>
                      <div className="text-xs text-slate-500">
                        <span className="uppercase text-[10px] tracking-wider mr-2 text-slate-400">{label}</span>
                        {shortDate(r.created_at)}
                      </div>
                      {r.note && <div className="text-xs text-slate-400 mt-0.5">{r.note}</div>}
                    </div>
                    <div className="text-right text-xs text-slate-500 mono">bal after<br />{money(r.outstanding_after)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Record repayment">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Amount" type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} testId="repay-amount" />
          <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} testId="repay-note" />
          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-repayment-btn">Record</Button>
          </div>
        </form>
      </Modal>

      <Modal open={collectOpen} onClose={() => setCollectOpen(false)} title="Collect today" maxWidth="max-w-lg">
        {colDone ? (
          <div className="text-center py-6" data-testid="collect-today-done">
            <div className="text-emerald-700 heading text-lg font-semibold mb-2">Collection recorded ✓</div>
            <div className="mono text-sm text-slate-700 mb-1">Receipt {colDone.receipt_no}</div>
            <div className="text-slate-500 text-sm mb-4">Amount {money(colDone.amount)} · Outstanding now {money(loan.outstanding)}</div>
            <div className="flex justify-center gap-2">
              <Button variant="secondary" onClick={() => { setColDone(null); setCollectOpen(false); }}>Close</Button>
              <Button onClick={openCollectToday}>Add another</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitCollectToday} className="space-y-4">
            <div className="text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
              <span><span className="text-slate-400">Loan:</span> <span className="mono">{loan.loan_no || loan.id.slice(0, 8)}</span></span>
              <span><span className="text-slate-400">EMI:</span> <span className="mono font-semibold">{money(loan.emi)}</span></span>
              <span><span className="text-slate-400">Outstanding:</span> <span className="mono font-semibold text-rose-700">{money(loan.outstanding)}</span></span>
            </div>
            <Input label="Amount today" type="number" step="0.01" required
              testId="ct-amount" value={colForm.amount}
              onChange={(e) => setColForm({ ...colForm, amount: e.target.value })} />

            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Payment method</div>
              <div className="grid grid-cols-5 gap-1.5">
                {[
                  ["cash", "Cash"],
                  ["card", "Card"],
                  ["upi", "UPI"],
                  ["neft", "NEFT"],
                  ["rtgs", "RTGS"],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setColForm({ ...colForm, payment_method: v })}
                    data-testid={`ct-method-${v}`}
                    className={`text-center px-2 py-2 rounded-lg border text-xs font-semibold transition-colors ${
                      colForm.payment_method === v
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >{l}</button>
                ))}
              </div>
            </div>

            {colForm.payment_method === "cash" && (
              <details className="border border-slate-200 rounded-lg" data-testid="ct-denom-details">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700 bg-slate-50 select-none">
                  Cash denomination
                  {(() => {
                    const t = ["500","200","100","50","20","10","5"].reduce((s, d) => s + Number(colForm[`denom_${d}`] || 0) * Number(d), 0);
                    return t > 0 ? <span className="mono text-emerald-700 ml-2">₹{t}</span> : null;
                  })()}
                </summary>
                <div className="p-3 grid grid-cols-2 gap-2">
                  {["500","200","100","50","20","10","5"].map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm" data-testid={`ct-denom-${d}`}>
                      <span className="mono font-semibold text-slate-700 w-12">₹{d}</span>
                      <input
                        type="number" min="0" value={colForm[`denom_${d}`]}
                        onChange={(e) => setColForm({ ...colForm, [`denom_${d}`]: Math.max(0, Number(e.target.value) || 0) })}
                        className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 py-1 text-sm mono outline-none focus:border-emerald-500"
                      />
                    </label>
                  ))}
                </div>
              </details>
            )}
            {(colForm.payment_method === "card" || colForm.payment_method === "neft" || colForm.payment_method === "rtgs") && (
              <Input label={`${colForm.payment_method.toUpperCase()} reference / txn no.`}
                testId="ct-ref" value={colForm.reference_no}
                onChange={(e) => setColForm({ ...colForm, reference_no: e.target.value })} />
            )}
            <Input label="Note (optional)" testId="ct-note"
              value={colForm.note}
              onChange={(e) => setColForm({ ...colForm, note: e.target.value })} />

            {colErr && <div className="text-sm text-rose-600" data-testid="ct-err">{colErr}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={() => setCollectOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={colBusy} data-testid="ct-submit">
                {colBusy ? "Recording…" : "Record collection"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

function StatSm({ label, value, node, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">{label}</div>
      {node ? node : <div className={`mono heading text-xl font-semibold ${accent ? "text-emerald-700" : "text-slate-900"}`} data-testid={testId}>{value}</div>}
    </div>
  );
}
const typeLabel = (t) => ({ deposit: "Deposit-backed Loan", general: "General Loan", commodity: "Commodity Loan" }[t] || t);
